import React, { useEffect, useMemo, useState } from 'react';
import { Progress, Spin } from 'antd';
import { EchartsWithTheme, Tooltip, useTheme, Empty, DatePicker } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyOverviewEnergyMediumListPost,
  apiV2EnergyOverviewEnergyDataList2Post,
} from '@maxtropy/device-customer-apis-v2';
import { formatThousandAndUnit } from '@/utils/utils';
import dayjs, { Dayjs } from 'dayjs';
import titleIcon from '@/assets/images/title_icon.png';
import circleBig from '../../../../imgs/circle-big@2x.png';
import bgPieKey from '../../../../imgs/bg-pieKey@2x.png';
import classNames from 'classnames';
import styles from './index.module.scss';

// 饼图颜色
const PIE_COLORS = ['#4A90E2', '#52E7FF', '#FFCB47', '#FF6D2C', '#CE90D1', '#16DD8E', '#ABD335', '#FF884C'];

const PIE_OUTER_SIZE = 180;
const PIE_CENTER_Y = 90;
const PIE_LEGEND_PAGE_SIZE = 4;

type PieDatum = {
  name: string;
  value: number;
  amount: number;
  unit?: string;
  color: string;
  legendKey: string;
};

const formatCenterValue = (data: Array<{ amount: number }>) =>
  `${(data.reduce((sum, item) => sum + item.amount, 0) / 10000).toFixed(1)}万`;

const getPieUnit = (data: Array<{ unit?: string }>) => data.find(item => item.unit)?.unit || '';

const formatAmountWithUnit = (amount?: number, unit?: string) => {
  if (amount == null) return '-';
  const formatted = formatThousandAndUnit(amount, 1);
  return `${formatted.value}${formatted.unit}${unit ? ` ${unit}` : ''}`;
};

const formatPercent = (value?: number) => `${(value ?? 0).toFixed(2)}%`;

const formatPlanValueWithUnit = (amount?: number, unit?: string) => {
  const formatted = formatThousandAndUnit(amount, 1);
  return `${formatted.value}${formatted.unit}${unit || ''}`;
};

// 创建饼图 option
const createPieOption = (data: PieDatum[], title: string) => {
  return {
    color: data.map(d => d.color),
    tooltip: {
      confine: true,
      formatter: (params: any) => `
        <div style="min-width:140px;">
          <div style="display:flex;gap:8px;align-items:center;">
            <span>${params.marker}</span>
            <span>${params.name}</span>
            <span style="font-weight:600;">${formatPercent(params.value)}</span>
          </div>
        </div>
      `,
    },
    title: {
      text: title,
      textStyle: {
        fontSize: 14,
        fontWeight: 400,
      },
      subtext: formatCenterValue(data),
      subtextStyle: {
        fontSize: 20,
        color: '#fff',
        fontWeight: 600,
      },
      left: PIE_OUTER_SIZE / 2,
      top: PIE_CENTER_Y - 20,
      itemGap: 4,
      textAlign: 'center',
    },
    legend: {
      show: false,
    },
    series: [
      {
        type: 'pie',
        radius: [58, PIE_OUTER_SIZE / 2],
        center: [PIE_OUTER_SIZE / 2, PIE_CENTER_Y],
        padAngle: 2,
        itemStyle: {
          borderRadius: 6,
        },
        label: { show: false },
        emphasis: {
          label: { show: false },
        },
        data: data.map(d => ({ name: d.name, value: d.value })),
      },
    ],
  };
};

const PieLegendName: React.FC<{ name: string }> = ({ name }) => {
  const needMarquee = name.length > 9;

  return (
    <Tooltip title={name}>
      <span className={styles.pieLegendName}>
        {needMarquee ? (
          <span className={styles.pieLegendNameTrack}>
            <span>{name}</span>
            <span>{name}</span>
          </span>
        ) : (
          <span className={styles.pieLegendNameText}>{name}</span>
        )}
      </span>
    </Tooltip>
  );
};

const PieLegend: React.FC<{
  data: PieDatum[];
  disabledKeys: string[];
  onToggle: (legendKey: string) => void;
}> = ({ data, disabledKeys, onToggle }) => {
  const [page, setPage] = useState(0);
  const totalPage = Math.max(Math.ceil(data.length / PIE_LEGEND_PAGE_SIZE), 1);
  const disabledKeySet = useMemo(() => new Set(disabledKeys), [disabledKeys]);

  useEffect(() => {
    setPage(0);
  }, [data]);

  const currentPage = Math.min(page, totalPage - 1);
  const visibleData = data.slice(currentPage * PIE_LEGEND_PAGE_SIZE, (currentPage + 1) * PIE_LEGEND_PAGE_SIZE);

  return (
    <div className={styles.pieLegend}>
      <div className={styles.pieLegendList}>
        {visibleData.map((item, index) => (
          <div
            key={`${item.legendKey}-${currentPage}-${index}`}
            className={classNames(
              styles.pieLegendItem,
              disabledKeySet.has(item.legendKey) ? styles.pieLegendItemDisabled : ''
            )}
            role="button"
            tabIndex={0}
            onClick={() => onToggle(item.legendKey)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggle(item.legendKey);
              }
            }}
          >
            <span className={styles.pieLegendDot} style={{ backgroundColor: item.color }} />
            <PieLegendName name={item.name} />
            <span className={styles.pieLegendPercent}>{formatPercent(item.value)}</span>
            <span className={styles.pieLegendAmount}>{formatAmountWithUnit(item.amount, item.unit)}</span>
          </div>
        ))}
      </div>
      {totalPage > 1 && (
        <div className={styles.pieLegendPager}>
          <button
            type="button"
            className={styles.pieLegendPagerBtn}
            disabled={currentPage === 0}
            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
          >
            ▲
          </button>
          <span>
            {currentPage + 1}/{totalPage}
          </span>
          <button
            type="button"
            className={styles.pieLegendPagerBtn}
            disabled={currentPage >= totalPage - 1}
            onClick={() => setPage(prev => Math.min(prev + 1, totalPage - 1))}
          >
            ▼
          </button>
        </div>
      )}
    </div>
  );
};

const PieKeyName: React.FC<{ name?: string }> = ({ name }) => (
  <Tooltip title={name || '-'}>
    <span className={styles.pieKeyName}>{name || '-'}</span>
  </Tooltip>
);

const calcPlanProgress = (value?: number, maxValue?: number) => {
  if (value == null || !maxValue) return 0;
  return Number(Math.min((value / maxValue) * 100, 100).toFixed(2));
};

type PlanType = 'dept' | 'area';

type UsageItem = {
  energyUnitId?: number;
  energyUnitName?: string;
  areaName?: string;
  deptName?: string;
  actualValue?: number;
  planValue?: number;
  proportion?: number;
  unitName?: string;
};

const getUsageDisplayName = (item: UsageItem, type: PlanType) => {
  return (type === 'dept' ? item.deptName : item.areaName) || item.energyUnitName || '-';
};

const calculateUsageRatios = (list: UsageItem[], type: PlanType) => {
  const total = list.reduce((sum, item) => sum + (Number(item.actualValue) || 0), 0);
  return list.map(item => {
    const amount = Number(item.actualValue) || 0;
    return {
      name: getUsageDisplayName(item, type),
      amount,
      unit: item.unitName || '',
      value: total ? Number(((amount / total) * 100).toFixed(2)) : 0,
    };
  });
};

const toggleLegendKey = (keys: string[], legendKey: string) =>
  keys.includes(legendKey) ? keys.filter(key => key !== legendKey) : [...keys, legendKey];

const filterPieDataByLegend = (data: PieDatum[], disabledKeys: string[]) => {
  const disabledKeySet = new Set(disabledKeys);
  return data.filter(item => !disabledKeySet.has(item.legendKey));
};

const EnergyComparisonModal: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<number>(1);
  const [queryMonth, setQueryMonth] = useState<Dayjs>(dayjs());
  const [planType, setPlanType] = useState<PlanType>('dept');
  const [disabledDeptLegendKeys, setDisabledDeptLegendKeys] = useState<string[]>([]);
  const [disabledAreaLegendKeys, setDisabledAreaLegendKeys] = useState<string[]>([]);

  // 介质列表
  const { data: mediumListData } = useRequest(() => apiV2EnergyOverviewEnergyMediumListPost({}));

  const planTabs = useMemo(() => {
    return (mediumListData?.list || []).map(item => ({
      key: item.energyMediumId!,
      label: item.energyMediumName,
    }));
  }, [mediumListData]);

  useEffect(() => {
    if (planTabs.length > 0 && !planTabs.find(t => t.key === activeTab)) {
      setActiveTab(planTabs[0].key);
    }
  }, [planTabs]);

  // 用能数据（部门 + 区域）
  const { data: energyData, loading: energyDataLoading } = useRequest(
    async () => {
      if (!activeTab) return null;
      const fromTime = queryMonth.startOf('month').valueOf();
      const toTime = queryMonth.endOf('month').valueOf();
      const res = await apiV2EnergyOverviewEnergyDataList2Post({
        electricMediumId: activeTab,
        fromTime,
        toTime,
        timeResolution: 'MONTH',
      });
      return res;
    },
    {
      ready: !!activeTab && !!queryMonth,
      refreshDeps: [activeTab, queryMonth],
    }
  );

  // 部门用能占比数据
  const deptRatioData = useMemo(() => {
    return calculateUsageRatios(energyData?.deptUsage || [], 'dept')
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        color: PIE_COLORS[index % PIE_COLORS.length],
        legendKey: `${item.name}-${index}`,
      }));
  }, [energyData]);

  // 区域用能占比数据
  const areaRatioData = useMemo(() => {
    return calculateUsageRatios(energyData?.areaUsage || [], 'area')
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        color: PIE_COLORS[index % PIE_COLORS.length],
        legendKey: `${item.name}-${index}`,
      }));
  }, [energyData]);

  useEffect(() => {
    setDisabledDeptLegendKeys([]);
  }, [deptRatioData]);

  useEffect(() => {
    setDisabledAreaLegendKeys([]);
  }, [areaRatioData]);

  const visibleDeptRatioData = useMemo(
    () => filterPieDataByLegend(deptRatioData, disabledDeptLegendKeys),
    [deptRatioData, disabledDeptLegendKeys]
  );

  const visibleAreaRatioData = useMemo(
    () => filterPieDataByLegend(areaRatioData, disabledAreaLegendKeys),
    [areaRatioData, disabledAreaLegendKeys]
  );

  const deptPieOption = useMemo(
    () =>
      createPieOption(
        visibleDeptRatioData,
        `生产部门${getPieUnit(deptRatioData) ? `(${getPieUnit(deptRatioData)})` : ''}`
      ),
    [deptRatioData, visibleDeptRatioData]
  );
  const areaPieOption = useMemo(
    () =>
      createPieOption(
        visibleAreaRatioData,
        `车间用能${getPieUnit(areaRatioData) ? `(${getPieUnit(areaRatioData)})` : ''}`
      ),
    [areaRatioData, visibleAreaRatioData]
  );

  // 最高/最低占比
  const deptHighest = deptRatioData[0];
  const deptLowest = deptRatioData[deptRatioData.length - 1];
  const areaHighest = areaRatioData[0];
  const areaLowest = areaRatioData[areaRatioData.length - 1];

  // 用能计划数据（按 planValue 降序）
  const planData = useMemo(() => {
    const list = planType === 'dept' ? energyData?.deptUsage || [] : energyData?.areaUsage || [];
    return [...list]
      .sort((a, b) => (b.planValue || 0) - (a.planValue || 0))
      .map(item => ({
        id: item.energyUnitId!,
        name: getUsageDisplayName(item, planType),
        planValue: item.planValue || 0,
        actualValue: item.actualValue || 0,
        unit: item.unitName || '',
        proportion: item.proportion || 0,
      }));
  }, [energyData, planType]);

  const maxPlanValue = useMemo(() => Math.max(...planData.map(d => d.planValue || 0)), [planData]);

  return (
    <Spin spinning={energyDataLoading} wrapperClassName={styles.spinWrapper}>
      <div className={styles.wrapper}>
        {/* 顶部：月份选择器 + 介质切换 */}
        <div className={styles.header}>
          <DatePicker
            picker="month"
            value={queryMonth}
            className={styles.datePicker}
            allowClear={false}
            disabledDate={current => !!current && current.isAfter(dayjs(), 'month')}
            onChange={v => v && setQueryMonth(v)}
          />
          <div className={styles.tabButtons}>
            {planTabs.map(tab => (
              <div
                key={tab.key}
                className={`${styles.tabBtn} ${activeTab === tab.key ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        </div>

        {/* 主体：左侧占比 + 右侧用能计划 */}
        <div className={styles.container}>
          {/* 左侧：占比区域 */}
          <div className={styles.leftSection}>
            {/* 部门用能占比 */}
            <div className={styles.ratioSection}>
              <div className={styles.ratioTitle}>
                <img src={titleIcon} alt="" className={styles.titleIcon} />
                <span>部门用能占比</span>
              </div>

              <div className={styles.pieChartWrapper}>
                <img src={circleBig} alt="" className={styles.pieCenterBg} />
                <EchartsWithTheme
                  option={deptPieOption}
                  theme={theme}
                  type="pie"
                  style={{ position: 'relative', zIndex: 1, width: '100%', height: 180 }}
                />
                <PieLegend
                  data={deptRatioData}
                  disabledKeys={disabledDeptLegendKeys}
                  onToggle={legendKey => setDisabledDeptLegendKeys(prev => toggleLegendKey(prev, legendKey))}
                />
              </div>

              {/* 最高/最低占比卡片 */}
              <div className={styles.pieKeyCards}>
                <div
                  className={`${styles.pieKeyCard} ${styles.pieKeyCardHighest}`}
                  style={{ backgroundImage: `url(${bgPieKey})` }}
                >
                  <span className={styles.pieKeyLabel}>最高占比</span>
                  <PieKeyName name={deptHighest?.name} />
                  <span className={styles.pieKeyPercent}>({formatPercent(deptHighest?.value)})</span>
                  <span className={styles.pieKeyValue}>
                    {formatAmountWithUnit(deptHighest?.amount, deptHighest?.unit)}
                  </span>
                </div>
                <div
                  className={`${styles.pieKeyCard} ${styles.pieKeyCardLowest}`}
                  style={{ backgroundImage: `url(${bgPieKey})` }}
                >
                  <span className={styles.pieKeyLabel}>最低占比</span>
                  <PieKeyName name={deptLowest?.name} />
                  <span className={styles.pieKeyPercent}>({formatPercent(deptLowest?.value)})</span>
                  <span className={styles.pieKeyValue}>
                    {formatAmountWithUnit(deptLowest?.amount, deptLowest?.unit)}
                  </span>
                </div>
              </div>
            </div>

            {/* 区域用能占比 */}
            <div className={styles.ratioSection}>
              <div className={styles.ratioTitle}>
                <img src={titleIcon} alt="" className={styles.titleIcon} />
                <span>区域用能占比</span>
              </div>
              <div className={styles.pieChartWrapper}>
                <img src={circleBig} alt="" className={styles.pieCenterBg} />
                <EchartsWithTheme
                  option={areaPieOption}
                  theme={theme}
                  type="pie"
                  style={{ position: 'relative', zIndex: 1, width: '100%', height: 180 }}
                />
                <PieLegend
                  data={areaRatioData}
                  disabledKeys={disabledAreaLegendKeys}
                  onToggle={legendKey => setDisabledAreaLegendKeys(prev => toggleLegendKey(prev, legendKey))}
                />
              </div>

              {/* 最高/最低占比卡片 */}
              <div className={styles.pieKeyCards}>
                <div
                  className={`${styles.pieKeyCard} ${styles.pieKeyCardHighest}`}
                  style={{ backgroundImage: `url(${bgPieKey})` }}
                >
                  <span className={styles.pieKeyLabel}>最高占比</span>
                  <PieKeyName name={areaHighest?.name} />
                  <span className={styles.pieKeyPercent}>({formatPercent(areaHighest?.value)})</span>
                  <span className={styles.pieKeyValue}>
                    {formatAmountWithUnit(areaHighest?.amount, areaHighest?.unit)}
                  </span>
                </div>
                <div
                  className={`${styles.pieKeyCard} ${styles.pieKeyCardLowest}`}
                  style={{ backgroundImage: `url(${bgPieKey})` }}
                >
                  <span className={styles.pieKeyLabel}>最低占比</span>
                  <PieKeyName name={areaLowest?.name} />
                  <span className={styles.pieKeyPercent}>({formatPercent(areaLowest?.value)})</span>
                  <span className={styles.pieKeyValue}>
                    {formatAmountWithUnit(areaLowest?.amount, areaLowest?.unit)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <div className={styles.divider} />

          {/* 右侧：用能计划区域 */}
          <div className={styles.rightSection}>
            {/* 标题行 + 部门/区域切换 */}
            <div className={styles.planTitleRow}>
              <div className={styles.planTitle}>
                <img src={titleIcon} alt="" className={styles.titleIcon} />
                <span>用能计划</span>
              </div>
              <div className={styles.planTypeToggle}>
                <div
                  className={`${styles.toggleBtn} ${planType === 'dept' ? styles.toggleActive : ''}`}
                  onClick={() => setPlanType('dept')}
                >
                  部门
                </div>
                <div
                  className={`${styles.toggleBtn} ${planType === 'area' ? styles.toggleActive : ''}`}
                  onClick={() => setPlanType('area')}
                >
                  区域
                </div>
              </div>
            </div>

            {/* 列表 */}
            <div className={styles.listWrapper}>
              {planData.length === 0 ? (
                <Empty style={{ padding: 40 }} />
              ) : (
                planData.map(item => {
                  const proportion = Number(((item.proportion || 0) * 100).toFixed(1));
                  return (
                    <div key={item.id} className={styles.listItem}>
                      {/* 名称 */}
                      <div className={styles.namePart}>
                        <Tooltip title={item.name}>
                          <span className={styles.nameText}>{item.name}</span>
                        </Tooltip>
                      </div>

                      {/* 计划/实际进度条 */}
                      <div className={styles.dataPart}>
                        <div className={styles.dataRow}>
                          <span className={styles.dataLabel}>计划</span>
                          <Progress
                            percent={calcPlanProgress(item.planValue, maxPlanValue)}
                            strokeColor="#FFCB47"
                            trailColor="rgba(255,203,71,0.15)"
                            showInfo={false}
                            size="small"
                            className={styles.progressBar}
                          />
                          <span className={styles.dataValue}>{formatPlanValueWithUnit(item.planValue, item.unit)}</span>
                        </div>
                        <div className={styles.dataRow}>
                          <span className={styles.dataLabel}>实际</span>
                          <Progress
                            percent={calcPlanProgress(item.actualValue, maxPlanValue)}
                            strokeColor="#52E7FF"
                            trailColor="rgba(82,231,255,0.15)"
                            showInfo={false}
                            size="small"
                            className={styles.progressBar}
                          />
                          <span className={styles.dataValue}>
                            {formatPlanValueWithUnit(item.actualValue, item.unit)}
                          </span>
                        </div>
                      </div>

                      {/* 分割线 */}
                      <div className={styles.itemDivider} />

                      {/* 占比 */}
                      <div className={styles.ratioPart}>
                        <span className={styles.ratioLabel}>占比:</span>
                        <Progress
                          className={styles.ratioProgress}
                          type="circle"
                          percent={Math.min(Math.max(proportion, 0), 100)}
                          size={64}
                          strokeWidth={11}
                          strokeColor="#65F25B"
                          trailColor="rgba(101, 242, 91, 0.16)"
                          format={() => <span className={styles.ratioProgressText}>{`${proportion}%`}</span>}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default EnergyComparisonModal;
